import React, { useState, useMemo } from 'react';
import { Utensils, Coffee, ShoppingBag, Receipt, Car, Plane, Users, MessageSquareQuote, Home, Zap, Film } from 'lucide-react';

export interface WebPaymentHeroCardProps {
    splitTitle?: string;
    organizerName?: string;
    organizerPhotoUrl?: string | null;
    peopleCount?: number;
    totalAmount?: number | null;
    assignedAmount?: number | null;
    note?: string | null;
    createdAt?: any;
    receiptThumbnailURL?: string | null;
    receiptImageURL?: string | null;
    merchantLogoUrl?: string | null;
    sessionIcon?: string | null;
    sessionIconColor?: string | null;
    loading?: boolean;
    className?: string;
    onViewReceipt?: () => void;
}

const GENERIC_HEURISTIC_WORDS = new Set([
    'dinner', 'lunch', 'breakfast', 'brunch', 'food', 'drinks', 'drink', 'meal', 'meals',
    'test', 'testpartial', 'partial', 'split', 'splits', 'rent', 'bill', 'bills',
    'party', 'groceries', 'grocery', 'misc', 'other', 'expense', 'expenses', 'trip', 'travel',
    'coffee', 'cafe', 'bar', 'movie', 'movies', 'concert', 'event', 'game', 'games',
    'manual', 'manualtest', 'owe', 'owed', 'pay', 'paid', 'share', 'shared', 'hangout',
    'weekend', 'birthday', 'vacation', 'flight', 'utilities', 'utility', 'general',
]);

export const isPdfMediaUri = (value: unknown): boolean => {
    const raw = String(value || '').trim().toLowerCase();
    if (!raw) return false;
    return raw.includes('.pdf') || raw.includes('application%2fpdf') || raw.startsWith('data:application/pdf');
};

export const CLIENT_BRAND_DOMAIN_MAP: Record<string, string> = {
    apple: 'apple.com',
    'apple music': 'apple.com',
    'apple one': 'apple.com',
    'apple tv': 'apple.com',
    'apple tv plus': 'apple.com',
    'apple tv+': 'apple.com',
    icloud: 'apple.com',
    'icloud plus': 'apple.com',
    'icloud+': 'apple.com',
    uber: 'uber.com',
    'uber eats': 'ubereats.com',
    ubereats: 'ubereats.com',
    doordash: 'doordash.com',
    door_dash: 'doordash.com',
    instacart: 'instacart.com',
    lyft: 'lyft.com',
    spotify: 'spotify.com',
    'spotify family': 'spotify.com',
    'spotify premium': 'spotify.com',
    'spotify duo': 'spotify.com',
    'spotify student': 'spotify.com',
    costco: 'costco.com',
    chipotle: 'chipotle.com',
    subway: 'subway.com',
    'shake shack': 'shakeshack.com',
    stubhub: 'stubhub.com',
    waymo: 'waymo.com',
    youtube: 'youtube.com',
    'youtube premium': 'youtube.com',
    'youtube tv': 'tv.youtube.com',
    'youtube music': 'youtube.com',
    google: 'google.com',
    'google one': 'google.com',
    amazon: 'amazon.com',
    'amazon prime': 'amazon.com',
    'prime video': 'amazon.com',
    walmart: 'walmart.com',
    'walmart plus': 'walmart.com',
    target: 'target.com',
    safeway: 'safeway.com',
    "trader joe's": 'traderjoes.com',
    'trader joes': 'traderjoes.com',
    hulu: 'hulu.com',
    'american airlines': 'aa.com',
    'british airways': 'britishairways.com',
    openai: 'openai.com',
    chatgpt: 'openai.com',
    'chatgpt plus': 'openai.com',
    'chat gpt': 'openai.com',
    claude: 'anthropic.com',
    'claude pro': 'anthropic.com',
    anthropic: 'anthropic.com',
    disney: 'disneyplus.com',
    'disney plus': 'disneyplus.com',
    'disney+': 'disneyplus.com',
    paramount: 'paramountplus.com',
    'paramount plus': 'paramountplus.com',
    'paramount+': 'paramountplus.com',
    peacock: 'peacocktv.com',
    'peacock tv': 'peacocktv.com',
    'peacock premium': 'peacocktv.com',
    max: 'max.com',
    'hbo max': 'max.com',
    hbo: 'max.com',
    audible: 'audible.com',
    crunchyroll: 'crunchyroll.com',
    'new york times': 'nytimes.com',
    'the new york times': 'nytimes.com',
    nyt: 'nytimes.com',
    'wall street journal': 'wsj.com',
    wsj: 'wsj.com',
    bloomberg: 'bloomberg.com',
    dropbox: 'dropbox.com',
    github: 'github.com',
    figma: 'figma.com',
    notion: 'notion.so',
    slack: 'slack.com',
    zoom: 'zoom.us',
    canva: 'canva.com',
    duolingo: 'duolingo.com',
    peloton: 'onepeloton.com',
    'whole foods': 'wholefoodsmarket.com',
    'whole foods market': 'wholefoodsmarket.com',
    cava: 'cava.com',
    nike: 'nike.com',
    sephora: 'sephora.com',
    starbucks: 'starbucks.com',
    netflix: 'netflix.com',
    robinhood: 'robinhood.com',
    grubhub: 'grubhub.com',
    postmates: 'postmates.com',
    caviar: 'trycaviar.com',
    seamless: 'seamless.com',
    qfc: 'qfc.com',
    usmobile: 'usmobile.com',
    'us mobile': 'usmobile.com',
};

export const SUBSCRIPTION_PLAN_SUFFIX_PATTERN = /\b(premium|family|student|plus|\+|pro|duo|individual|personal|solo|unlimited|basic|standard|tier|plan|plans|subscription|subscriptions|membership|memberships|annual|monthly|yearly|pass)\b/gi;

export function stripSubscriptionPlanTokens(text: string): string {
    return String(text || '')
        .replace(SUBSCRIPTION_PLAN_SUFFIX_PATTERN, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

export const deriveClientMerchantLogoUrl = (title: string | null | undefined): string | null => {
    const raw = String(title || '').trim();
    if (!raw) return null;

    const clean = raw
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, ' ')
        .replace(/\b(inc|llc|corp|corporation|co|company|ltd|restaurant|cafe|bar|market|store|group|holdings)\b/g, ' ')
        .replace(/\b(online|purchase|payment|debit|credit|pos|checkcard|visa|mastercard|amex|discover)\b/g, ' ')
        .replace(/\d+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    if (!clean) return null;

    // Direct brand match
    if (CLIENT_BRAND_DOMAIN_MAP[clean]) {
        return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(CLIENT_BRAND_DOMAIN_MAP[clean])}&sz=128`;
    }

    // Suffix/prefix brand match (longest brand key first e.g. "Uber Eats" before "Uber")
    const sortedBrandEntries = Object.entries(CLIENT_BRAND_DOMAIN_MAP).sort((a, b) => b[0].length - a[0].length);
    for (const [key, domain] of sortedBrandEntries) {
        if (clean === key || clean.startsWith(`${key} `) || clean.endsWith(` ${key}`) || clean.includes(` ${key} `)) {
            return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=128`;
        }
    }

    // Strip subscription plan modifiers
    const strippedPlan = stripSubscriptionPlanTokens(clean);
    if (strippedPlan && strippedPlan !== clean) {
        if (CLIENT_BRAND_DOMAIN_MAP[strippedPlan]) {
            return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(CLIENT_BRAND_DOMAIN_MAP[strippedPlan])}&sz=128`;
        }
        for (const [key, domain] of sortedBrandEntries) {
            if (strippedPlan === key || strippedPlan.startsWith(`${key} `) || strippedPlan.endsWith(` ${key}`) || strippedPlan.includes(` ${key} `)) {
                return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=128`;
            }
        }
    }

    return null;
};

export const resolveCategoryIcon = (title: string, sessionIcon?: string | null) => {
    const iconKey = String(sessionIcon || '').toLowerCase().trim();
    if (iconKey === 'utensils' || iconKey === 'dining' || iconKey === 'food') return Utensils;
    if (iconKey === 'coffee') return Coffee;
    if (iconKey === 'shopping-bag' || iconKey === 'store' || iconKey === 'shopping' || iconKey === 'groceries') return ShoppingBag;
    if (iconKey === 'car' || iconKey === 'ride') return Car;
    if (iconKey === 'plane' || iconKey === 'travel' || iconKey === 'flight') return Plane;
    if (iconKey === 'home' || iconKey === 'house' || iconKey === 'rent') return Home;
    if (iconKey === 'zap' || iconKey === 'utilities' || iconKey === 'power') return Zap;
    if (iconKey === 'film' || iconKey === 'entertainment' || iconKey === 'movie') return Film;
    if (iconKey === 'users' || iconKey === 'social' || iconKey === 'group') return Users;
    if (iconKey === 'receipt' || iconKey === 'general') return Receipt;

    const lower = (title || '').toLowerCase();
    if (lower.includes('coffee') || lower.includes('cafe') || lower.includes('bakery') || lower.includes('starbucks') || lower.includes('matcha')) {
        return Coffee;
    }
    if (
        lower.includes('restaurant') || lower.includes('diner') || lower.includes('bistro') ||
        lower.includes('grill') || lower.includes('soleil') || lower.includes('bar') ||
        lower.includes('pizza') || lower.includes('sushi') || lower.includes('taco') ||
        lower.includes('kitchen') || lower.includes('food') || lower.includes('dinner') ||
        lower.includes('lunch') || lower.includes('breakfast') || lower.includes('brunch')
    ) {
        return Utensils;
    }
    if (lower.includes('uber') || lower.includes('lyft') || lower.includes('ride') || lower.includes('gas') || lower.includes('parking')) {
        return Car;
    }
    if (lower.includes('flight') || lower.includes('hotel') || lower.includes('airbnb') || lower.includes('trip')) {
        return Plane;
    }
    if (lower.includes('groceries') || lower.includes('market') || lower.includes('target') || lower.includes('costco') || lower.includes('trader')) {
        return ShoppingBag;
    }
    if (lower.includes('rent') || lower.includes('electric') || lower.includes('wifi') || lower.includes('internet') || lower.includes('utility') || lower.includes('power')) {
        return Zap;
    }
    if (lower.includes('movie') || lower.includes('cinema') || lower.includes('concert') || lower.includes('tickets') || lower.includes('show')) {
        return Film;
    }
    return Receipt;
};

const formatTimeAgo = (value: any): string => {
    if (!value) return '';
    try {
        const date = value?.toDate ? value.toDate() : new Date(value?.seconds ? value.seconds * 1000 : value);
        if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
        const now = Date.now();
        const diffSec = Math.floor((now - date.getTime()) / 1000);
        if (diffSec < 60) return 'just now';
        const diffMin = Math.floor(diffSec / 60);
        if (diffMin < 60) return `${diffMin}m ago`;
        const diffHours = Math.floor(diffMin / 60);
        if (diffHours < 24) return `${diffHours}h ago`;
        const diffDays = Math.floor(diffHours / 24);
        if (diffDays < 7) return `${diffDays}d ago`;
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch {
        return '';
    }
};

export const WebPaymentHeroCard: React.FC<WebPaymentHeroCardProps> = ({
    splitTitle,
    organizerName,
    organizerPhotoUrl,
    peopleCount,
    totalAmount,
    assignedAmount,
    note,
    createdAt,
    receiptThumbnailURL,
    receiptImageURL,
    merchantLogoUrl,
    sessionIcon,
    sessionIconColor,
    loading = false,
    className = '',
    onViewReceipt,
}) => {
    const [failedReceiptUri, setFailedReceiptUri] = useState(false);
    const [failedLogoUri, setFailedLogoUri] = useState(false);
    const [failedOrgPhoto, setFailedOrgPhoto] = useState(false);
    const [showInternalReceiptModal, setShowInternalReceiptModal] = useState(false);

    const title = (splitTitle || 'Payment Request').trim();
    const CategoryIcon = resolveCategoryIcon(title, sessionIcon);
    const rawOrgName = (organizerName || '').trim();
    const orgInitial = useMemo(() => {
        if (!rawOrgName) return 'O';
        const parts = rawOrgName.split(/\s+/).filter(Boolean);
        if (parts.length >= 2) {
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }
        return rawOrgName.slice(0, 2).toUpperCase();
    }, [rawOrgName]);

    const orgFirstName = useMemo(() => {
        if (!rawOrgName) return '';
        return rawOrgName.split(/\s+/)[0];
    }, [rawOrgName]);

    const rawReceiptUri = receiptImageURL || receiptThumbnailURL;
    const isReceiptPdf = isPdfMediaUri(rawReceiptUri);
    const validReceiptUri = !isReceiptPdf && rawReceiptUri && !rawReceiptUri.startsWith('file://') ? rawReceiptUri : null;

    const derivedLogoUri = (!merchantLogoUrl && title) ? deriveClientMerchantLogoUrl(title) : null;
    const validLogoUri = (merchantLogoUrl && !merchantLogoUrl.startsWith('file://')) ? merchantLogoUrl : derivedLogoUri;

    const activeImageUri = useMemo(() => {
        if (validReceiptUri && !failedReceiptUri) {
            return { uri: validReceiptUri, type: 'receipt' as const };
        }
        if (validLogoUri && !failedLogoUri) {
            return { uri: validLogoUri, type: 'logo' as const };
        }
        return null;
    }, [validReceiptUri, failedReceiptUri, validLogoUri, failedLogoUri]);

    const handleReceiptClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (onViewReceipt) {
            onViewReceipt();
        } else if (validReceiptUri) {
            setShowInternalReceiptModal(true);
        }
    };

    if (loading) {
        return (
            <div className={`rounded-[2.25rem] bg-white p-6 sm:p-8 text-center animate-pulse ${className}`}>
                <div className="flex flex-col items-center space-y-4 pt-1">
                    <div className="w-20 h-20 sm:w-22 sm:h-22 rounded-full bg-slate-100" />
                    <div className="h-4 w-32 bg-slate-100 rounded-full" />
                    <div className="h-7 w-48 bg-slate-100 rounded-xl" />
                    <div className="h-4 w-28 bg-slate-100 rounded-full" />
                </div>
            </div>
        );
    }

    const hasAssignedAmount = typeof assignedAmount === 'number' && Number.isFinite(assignedAmount) && assignedAmount > 0;
    const formattedAssignedAmount = hasAssignedAmount
        ? `$${assignedAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : null;

    const formattedTotalAmount = typeof totalAmount === 'number' && Number.isFinite(totalAmount) && totalAmount > 0
        ? `$${totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : null;

    const timeAgoLabel = formatTimeAgo(createdAt);

    return (
        <>
            <div className={`rounded-[2.25rem] bg-white p-6 sm:p-8 text-center relative space-y-4 shadow-none ${className}`}>
                {/* Top Merchant / Receipt Circle Avatar with Organizer Badge */}
                <div className="flex justify-center pt-1">
                    <div className="relative">
                        {/* Primary Merchant / Receipt Circle Avatar */}
                        {validReceiptUri ? (
                            <button
                                type="button"
                                onClick={handleReceiptClick}
                                className="w-20 h-20 sm:w-22 sm:h-22 rounded-full overflow-hidden border-4 border-slate-50 bg-teal-50 shadow-xs flex items-center justify-center text-teal-700 shrink-0 cursor-pointer hover:scale-105 transition-transform group relative focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
                                aria-label="View full receipt photo"
                                title="Tap to view receipt photo"
                            >
                                {activeImageUri?.type === 'receipt' ? (
                                    <img
                                        src={activeImageUri.uri}
                                        alt={title}
                                        className="w-full h-full object-cover group-hover:opacity-90 transition-opacity"
                                        onError={() => {
                                            setFailedReceiptUri(true);
                                        }}
                                    />
                                ) : (
                                    <div
                                        className="w-full h-full flex items-center justify-center"
                                        style={sessionIconColor ? { backgroundColor: sessionIconColor, color: '#FFFFFF' } : undefined}
                                    >
                                        <CategoryIcon className="w-8 h-8 stroke-[2.2]" />
                                    </div>
                                )}
                                <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                    <Receipt className="w-6 h-6 text-white drop-shadow-md" />
                                </div>
                            </button>
                        ) : (
                            <div className="w-20 h-20 sm:w-22 sm:h-22 rounded-full overflow-hidden border-4 border-slate-50 bg-teal-50 shadow-xs flex items-center justify-center text-teal-700 shrink-0">
                                {activeImageUri ? (
                                    <img
                                        src={activeImageUri.uri}
                                        alt={title}
                                        className="w-full h-full object-contain p-3"
                                        onError={() => {
                                            setFailedLogoUri(true);
                                        }}
                                    />
                                ) : (
                                    <div
                                        className="w-full h-full flex items-center justify-center"
                                        style={sessionIconColor ? { backgroundColor: sessionIconColor, color: '#FFFFFF' } : undefined}
                                    >
                                        <CategoryIcon className="w-8 h-8 stroke-[2.2]" />
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Organizer Mini Avatar Badge (matching Clero App AppAvatar token: border.soft #D7E1EA + text.secondary #475569) */}
                        {rawOrgName && (
                            <div
                                className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full border-2 border-white bg-[#D7E1EA] text-[#475569] text-[10px] font-black flex items-center justify-center overflow-hidden shadow-xs select-none"
                                title={`Organizer: ${rawOrgName}`}
                            >
                                {organizerPhotoUrl && !failedOrgPhoto ? (
                                    <img
                                        src={organizerPhotoUrl}
                                        alt={rawOrgName}
                                        className="w-full h-full object-cover"
                                        onError={() => setFailedOrgPhoto(true)}
                                    />
                                ) : (
                                    <span>{orgInitial}</span>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Context-Aware Headline & Amount */}
                <div className="space-y-1.5 pt-1">
                    {hasAssignedAmount && formattedAssignedAmount ? (
                        <div>
                            <p className="text-xs sm:text-sm font-bold text-slate-500">
                                {orgFirstName ? `${orgFirstName} requested` : 'Amount requested'}
                            </p>
                            <p className="text-4xl sm:text-5xl font-black tracking-tight text-slate-950 tabular-nums my-1">
                                {formattedAssignedAmount}
                            </p>
                        </div>
                    ) : (
                        <div>
                            <p className="text-xs sm:text-sm font-bold text-slate-500">
                                {orgFirstName ? `${orgFirstName} sent you a request for` : 'Payment request for'}
                            </p>
                            <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-slate-950 truncate max-w-xs mx-auto mt-1">
                                {title}
                            </h2>
                        </div>
                    )}
                </div>

                {/* Merchant Title & Total Summary */}
                <div className="pt-1 pb-1 space-y-1">
                    {hasAssignedAmount && (
                        <h3 className="text-sm sm:text-base font-bold text-slate-900 tracking-tight truncate">
                            {title}
                        </h3>
                    )}
                    <div className="flex items-center justify-center gap-2 text-xs font-semibold text-slate-500">
                        {formattedTotalAmount && (
                            <span>Total {formattedTotalAmount}</span>
                        )}
                        {typeof peopleCount === 'number' && peopleCount > 0 && (
                            <>
                                {formattedTotalAmount && <span className="text-slate-300">•</span>}
                                <span>
                                    {peopleCount} {peopleCount === 1 ? 'person' : 'people'}
                                </span>
                            </>
                        )}
                        {timeAgoLabel && (
                            <>
                                <span className="text-slate-300">•</span>
                                <span>{timeAgoLabel}</span>
                            </>
                        )}
                    </div>
                </div>

                {/* Optional Note Card */}
                {note && String(note).trim() && (
                    <div className="rounded-2xl bg-[#F8FAFC] p-3.5 text-left flex items-start gap-2.5">
                        <MessageSquareQuote className="w-4 h-4 text-teal-600 shrink-0 mt-0.5" />
                        <div className="min-w-0 flex-1">
                            <p className="text-xs font-medium text-slate-700 leading-relaxed italic">
                                "{String(note).trim()}"
                            </p>
                            {orgFirstName && (
                                <p className="text-[10px] font-bold text-slate-400 mt-1 uppercase tracking-wider">
                                    — {orgFirstName}
                                </p>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Internal Receipt Modal */}
            {showInternalReceiptModal && validReceiptUri && (
                <div
                    className="fixed inset-0 z-50 bg-black/90 backdrop-blur-xl flex items-center justify-center p-4 animate-in fade-in duration-200"
                    onClick={() => setShowInternalReceiptModal(false)}
                >
                    <div className="relative max-w-lg w-full max-h-[90vh] overflow-hidden rounded-2xl flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                        <img
                            src={validReceiptUri}
                            alt="Receipt"
                            className="w-full h-auto object-contain max-h-[85vh] mx-auto rounded-xl"
                        />
                        <button
                            type="button"
                            onClick={() => setShowInternalReceiptModal(false)}
                            className="absolute top-4 right-4 text-white bg-black/60 hover:bg-black/80 p-2.5 rounded-full backdrop-blur-md transition-colors cursor-pointer"
                            aria-label="Close receipt"
                        >
                            <span className="text-xl font-bold leading-none">✕</span>
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default WebPaymentHeroCard;
